import { DateInput3 } from '@blueprintjs/datetime2';
import { Button, FormGroup, Intent } from '@blueprintjs/core';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { useState } from 'react';

import Flex from '@app/components/Flex';
import Heading from '@app/components/Heading';
import { ToastUtils } from '@app/data/utils';
import { usePageTitle } from '@app/hooks';
import { $authorizedUser } from '@containers/store/states';

import { $coreUser } from '../../store/states';

import { ALLOWED_REPORTS_EMAILS } from './consts';
import { fetchTimeTrackingReport } from './store/effects';
import { buildWorkbookBlob, downloadBlob } from './utils/buildWorkbook';

// API expects 'YYYY-MM-DD'
const formatApiDate = (date: Date): string => moment(date).format('YYYY-MM-DD');

// Filename label, e.g. 5-11-2026 (matches the supplied sample filename style)
const formatFileLabel = (date: Date | null): string => (date ? moment(date).format('M-D-YYYY') : 'all');

const CoreUserReports: React.FC = () => {
    usePageTitle('User Activity Reports');

    const me = useStore($authorizedUser);
    const coreUser = useStore($coreUser);
    const isSignedInReportsAllowedUser = ALLOWED_REPORTS_EMAILS.includes(me?.user.email?.trim().toLowerCase() ?? '');
    const isProfileReportsAllowedUser = ALLOWED_REPORTS_EMAILS.includes(coreUser?.email?.trim().toLowerCase() ?? '');

    // From Date is optional (blank => no lower bound); To Date defaults to today.
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(moment().toDate());
    const [isGenerating, setIsGenerating] = useState(false);

    // Defense-in-depth: only the allowed member profile may render this page (the tab is
    // also gated by viewed profile email, and the backend gates the API server-side).
    if (!isProfileReportsAllowedUser || !isSignedInReportsAllowedUser) {
        return null;
    }

    const handleDownload = async () => {
        if (!toDate) {
            ToastUtils.showToast({ message: 'Please select a "To Date".', intent: Intent.WARNING });
            return;
        }

        if (fromDate && moment(fromDate).isAfter(moment(toDate), 'day')) {
            ToastUtils.showToast({ message: '"From Date" must be on or before "To Date".', intent: Intent.WARNING });
            return;
        }

        setIsGenerating(true);

        try {
            const report = await fetchTimeTrackingReport({
                fromDate: fromDate ? formatApiDate(fromDate) : null,
                toDate: formatApiDate(toDate),
            });

            const blob = await buildWorkbookBlob(report.items, report.sub_tasks);
            const fileName = `user_activity_report_${formatFileLabel(fromDate)}_to_${formatFileLabel(toDate)}.xlsx`;
            downloadBlob(blob, fileName);

            if (report.items.length === 0) {
                ToastUtils.showToast({
                    message: 'No activity found for the selected date range. An empty report was downloaded.',
                    intent: Intent.PRIMARY,
                });
            }
        } catch (e) {
            console.error(e);
            ToastUtils.showToast({
                message: 'Failed to generate the report. Please try again.',
                intent: Intent.DANGER,
            });
        } finally {
            setIsGenerating(false);
        }
    };

    const renderDateInput = (
        value: Date | null,
        onChange: (date: Date | null) => void,
        placeholder: string,
        canClear: boolean
    ) => {
        return (
            <DateInput3
                fill
                highlightCurrentDay
                canClearSelection={canClear}
                placeholder={placeholder}
                value={value ? moment(value).format() : null}
                inputProps={{ leftIcon: 'calendar' }}
                minDate={moment('2018-01-01').toDate()}
                maxDate={moment().toDate()}
                dayPickerProps={{ firstWeekContainsDate: 1 }}
                formatDate={(date) => moment(date).format('MMM D, YYYY')}
                parseDate={(str) => new Date(str)}
                onChange={(newDate) => onChange(newDate ? moment(newDate).toDate() : null)}
            />
        );
    };

    return (
        <div>
            <Heading type="h3" className="mb-2">
                User Activity Reports
            </Heading>

            <Flex direction="row" align="flex-end" gap="16px">
                <div style={{ minWidth: '220px' }}>
                    <FormGroup className="mb-0" label="From Date:">
                        {renderDateInput(fromDate, setFromDate, 'No lower bound', true)}
                    </FormGroup>
                </div>

                <div style={{ minWidth: '220px' }}>
                    <FormGroup className="mb-0" label="To Date:">
                        {renderDateInput(toDate, setToDate, 'Pick a date', false)}
                    </FormGroup>
                </div>

                <Button
                    icon="download"
                    intent={Intent.PRIMARY}
                    loading={isGenerating}
                    disabled={!toDate}
                    onClick={handleDownload}
                >
                    Download
                </Button>
            </Flex>
        </div>
    );
};

export default CoreUserReports;
